import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Mail, ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import LogoIcon from '@/components/LogoIcon';
import { trackEvent, trackConversion } from '@/components/GoogleAnalytics';
import { trackEventMp, trackConversionMp } from '@/components/Mixpanel';
import { trackEvent as trackEventHj } from '@/components/Hotjar';

interface SubscriptionInfo {
  id: string;
  email?: string;
  maskedEmail?: string;
  subscription_status: string;
  email_notifications_enabled: boolean;
  isOwner: boolean;
}


export default function Unsubscribe() {
  const { subscriptionId } = useParams();
  const { t } = useTranslation();
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [comment, setComment] = useState('');
  const [actionCompleted, setActionCompleted] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  
  const UNSUBSCRIBE_REASONS = t('unsubscribe.reasons', { returnObjects: true }) as string[];

  useEffect(() => {
    if (subscriptionId) {
      loadSubscriptionInfo();
    }
  }, [subscriptionId]);

  const loadSubscriptionInfo = async () => {
    try {
      const sessionData = localStorage.getItem('newsletter_session');
      const sessionToken = sessionData ? JSON.parse(sessionData)?.session_token : null;
      
      const { data, error } = await supabase.functions.invoke('get-subscription-info', {
        body: { subscription_id: subscriptionId, session_token: sessionToken }
      });

      if (error) throw error;

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      setSubscriptionInfo(data);
    } catch (error: any) {
      console.error('Error loading subscription info:', error);
      toast.error(t('unsubscribe.errors.loadSubscriptionError'));
    } finally {
      setLoading(false);
    }
  };

  const sendMagicLink = async () => {
    if (!subscriptionInfo) return;

    try {
      setProcessing(true);
      
      // Track analytics event
      const eventParams = {
        app_version: '3.0',
        layout_version: 'interactive_preview',
        event_category: 'user_action',
        email: subscriptionInfo.maskedEmail
      };
      
      trackEvent('magic_link_requested', eventParams);
      trackEventMp('magic_link_requested', eventParams);
      trackEventHj('magic_link_requested');
      
      const { error } = await supabase.functions.invoke('generate-magic-link', {
        body: { 
          subscription_id: subscriptionId,
          redirect_to: `/unsubscribe/${subscriptionId}`
        }
      });

      if (error) throw error;

      setMagicLinkSent(true);
      toast.success(t('unsubscribe.toasts.linkSent'));
    } catch (error: any) {
      toast.error(error.message || t('unsubscribe.errors.sendLinkError'));
    } finally {
      setProcessing(false);
    }
  };

  const handleAction = async (action: 'pause' | 'unsubscribe' | 'resubscribe') => {
    if (!subscriptionInfo) return;

    if (action !== 'resubscribe' && selectedReasons.length === 0) {
      toast.error(t('unsubscribe.errors.selectReasonError'));
      return;
    }

    const sessionData = localStorage.getItem('newsletter_session');
    const sessionToken = sessionData ? JSON.parse(sessionData)?.session_token : null;
    if (!sessionToken) {
      toast.error(t('unsubscribe.errors.sessionExpired'));
      return;
    }

    try {
      setProcessing(true);
      
      // Track analytics event
      let eventName = '';
      if (action === 'pause') {
        eventName = 'email_notifications_paused';
      } else if (action === 'unsubscribe') {
        eventName = 'newsletter_unsubscribed';
      } else if (action === 'resubscribe') {
        eventName = 'email_notifications_reactivated';
      }
      
      const eventParams = {
        app_version: '3.0',
        layout_version: 'interactive_preview',
        event_category: action === 'unsubscribe' ? 'conversion' : 'user_action',
        action: action,
        reasons_count: selectedReasons.length,
        has_comment: comment.trim().length > 0,
        email: subscriptionInfo.email?.split('@')[0] + '@***'
      };
      
      trackEvent(eventName, eventParams);
      trackEventMp(eventName, eventParams);
      trackEventHj(eventName);
      
      const { data, error } = await supabase.functions.invoke('unsubscribe', {
        body: {
          subscriptionId: subscriptionInfo.id,
          action,
          reason: selectedReasons.join(', '),
          comment: comment.trim() || null,
          session_token: sessionToken
        }
      });

      if (error) throw error;

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      setActionCompleted(true);
      
      if (action === 'pause') {
        toast.success(t('unsubscribe.toasts.emailsPausedSuccess'));
      } else if (action === 'unsubscribe') {
        toast.success(t('unsubscribe.toasts.unsubscribeSuccess'));
      } else {
        toast.success(t('unsubscribe.toasts.emailsReactivatedSuccess'));
      }

    } catch (error: any) {
      toast.error(error.message || t('unsubscribe.errors.processError'));
    } finally {
      setProcessing(false);
    }
  };

  const handleReasonChange = (reason: string, checked: boolean) => {
    // Track analytics event for reason selection
    if (checked) {
      const eventParams = {
        app_version: '3.0',
        layout_version: 'interactive_preview',
        event_category: 'engagement',
        reason: reason,
        total_selected: selectedReasons.length + 1
      };
      
      trackEvent('unsubscribe_reason_selected', eventParams);
      trackEventMp('unsubscribe_reason_selected', eventParams);
      trackEventHj('unsubscribe_reason_selected');
    }
    
    setSelectedReasons(prev => 
      checked 
        ? [...prev, reason]
        : prev.filter(r => r !== reason)
    );
  };

  const handleOpenCustomerPortal = async () => {
    try {
      setIsPortalLoading(true);
      
      const sessionData = localStorage.getItem('newsletter_session');
      const sessionToken = sessionData ? JSON.parse(sessionData)?.session_token : null;
      
      if (!sessionToken) {
        toast.error(t('unsubscribe.errors.sessionExpired'));
        return;
      }
      
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        body: { session_token: sessionToken }
      });
      
      if (error || data?.error || !data?.url) {
        toast.error(data?.error || t('unsubscribe.errors.portalError'));
        return;
      }
      
      window.location.href = data.url;
    } catch (err) {
      toast.error(t('unsubscribe.errors.portalError'));
    } finally {
      setIsPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-newsletter-primary/5 to-newsletter-secondary/5 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-newsletter-primary"></div>
      </div>
    );
  }

  if (!subscriptionInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-newsletter-primary/5 to-newsletter-secondary/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <LogoIcon />
            </div>
            <CardTitle className="text-destructive">{t('unsubscribe.errors.subscriptionNotFound')}</CardTitle>
            <CardDescription>
              {t('unsubscribe.errors.invalidLink')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/">
              <Button className="w-full" variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('unsubscribe.navigation.backToHome')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!subscriptionInfo.isOwner) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-newsletter-primary/5 to-newsletter-secondary/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <LogoIcon />
            </div>
            <CardTitle>{t('unsubscribe.identity.title')}</CardTitle>
            <CardDescription>
              {t('unsubscribe.identity.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4" />
                <span className="font-medium">{subscriptionInfo.maskedEmail}</span>
              </div>
            </div>

            {magicLinkSent ? (
              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  {t('unsubscribe.identity.linkSentMessage')}
                </p>
                <Button 
                  variant="outline" 
                  onClick={sendMagicLink}
                  disabled={processing}
                >
                  {t('unsubscribe.identity.sendAgain')}
                </Button>
              </div>
            ) : (
              <Button 
                onClick={sendMagicLink}
                disabled={processing}
                className="w-full"
              >
                {processing ? t('unsubscribe.identity.sending') : t('unsubscribe.identity.sendLink')}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (actionCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-newsletter-primary/5 to-newsletter-secondary/5 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <LogoIcon />
            </div>
            <CardTitle>{t('unsubscribe.success.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <p className="text-muted-foreground">
              {t('unsubscribe.success.description')}
            </p>
            <div className="space-y-2">
              <Link to={`/preferences/${subscriptionInfo.id}`}>
                <Button className="w-full">
                  {t('unsubscribe.success.managePreferences')}
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('unsubscribe.navigation.backToHome')}
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasActiveStripeSubscription = subscriptionInfo.subscription_status === 'active';
  const isEmailsPaused = !subscriptionInfo.email_notifications_enabled;

  return (
    <div className="min-h-screen bg-gradient-to-br from-newsletter-primary/5 to-newsletter-secondary/5 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <div className="mx-auto mb-4">
            <LogoIcon />
          </div>
          <h1 className="text-2xl font-bold">{t('unsubscribe.management.title')}</h1>
          <p className="text-muted-foreground">{subscriptionInfo.email}</p>
        </div>

        {isEmailsPaused ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('unsubscribe.management.emailsPaused.title')}</CardTitle>
              <CardDescription>
                {t('unsubscribe.management.emailsPaused.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => handleAction('resubscribe')}
                disabled={processing}
                className="w-full"
              >
                {processing ? t('unsubscribe.management.processing') : t('unsubscribe.management.emailsPaused.reactivateButton')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>{t('unsubscribe.management.cancelOptions.title')}</CardTitle>
              <CardDescription>
                {hasActiveStripeSubscription 
                  ? t('unsubscribe.management.cancelOptions.activeSubscriptionDescription')
                  : t('unsubscribe.management.cancelOptions.generalDescription')
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Label className="text-base font-medium">{t('unsubscribe.management.reasonLabel')}</Label>
                <div className="grid grid-cols-1 gap-3">
                  {UNSUBSCRIBE_REASONS.map((reason) => (
                    <div key={reason} className="flex items-center space-x-2">
                      <Checkbox
                        id={reason}
                        checked={selectedReasons.includes(reason)}
                        onCheckedChange={(checked) => 
                          handleReasonChange(reason, checked as boolean)
                        }
                      />
                      <Label htmlFor={reason} className="text-sm font-normal">
                        {reason}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="comment">{t('unsubscribe.management.commentLabel')}</Label>
                <Textarea
                  id="comment"
                  placeholder={t('unsubscribe.management.commentPlaceholder')}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="space-y-3">
                {hasActiveStripeSubscription && (
                  <Button
                    onClick={() => handleAction('pause')}
                    disabled={processing || selectedReasons.length === 0}
                    className="w-full"
                    variant="default"
                  >
                    {processing ? t('unsubscribe.management.processing') : t('unsubscribe.management.pauseButton')}
                  </Button>
                )}

                {!hasActiveStripeSubscription && (
                  <Button
                    onClick={() => handleAction('unsubscribe')}
                    disabled={processing || selectedReasons.length === 0}
                    className="w-full"
                    variant="default"
                  >
                    {processing ? t('unsubscribe.management.processing') : t('unsubscribe.management.unsubscribeButton')}
                  </Button>
                )}

                {hasActiveStripeSubscription && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">
                      {t('unsubscribe.management.manageSubscription.description')}
                    </p>
                    <Button
                      onClick={handleOpenCustomerPortal}
                      disabled={isPortalLoading}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      {isPortalLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ExternalLink className="w-4 h-4 mr-2" />
                      )}
                      {t('unsubscribe.management.manageSubscription.button')}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="text-center">
          <Link to="/">
            <Button variant="ghost">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('unsubscribe.navigation.backToHome')}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}